// ============================================
// LIGA AJUPAM - Admin Authentication Service
// ============================================
// Manejo de autenticación segura para administradores

import { supabase, handleSupabaseError } from './client';

// Helper para llamar RPC functions con tipado flexible
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const callRpc = async (fnName: string, params: Record<string, any>): Promise<any> => {
  const { data, error } = await (supabase.rpc as any)(fnName, params);
  if (error) throw handleSupabaseError(error);
  return data;
};

interface AdminSessionInfo {
  userId: string;
  role: 'ADMIN' | 'SUPER_ADMIN' | 'VENUE_ADMIN';
  venueId: string | null;
}

/**
 * Valida las credenciales del admin/venue y crea una sesión (V3 Segura)
 * La validación ahora ocurre íntegramente en el servidor
 */
export const loginAdmin = async (
  username: string,
  password: string
): Promise<{ sessionToken: string; userId: string; role: 'ADMIN' | 'SUPER_ADMIN' | 'VENUE_ADMIN'; venueId: string | null }> => {
  try {
    const data = await callRpc('login_admin_v3', {
      p_username: username,
      p_password: password
    });

    // 🔍 DEBUG: Ver respuesta cruda del RPC
    console.log('🔍 DEBUG adminAuth.loginAdmin - Respuesta RPC completa:', data);
    console.log('🔍 DEBUG adminAuth.loginAdmin - data[0]:', data ? data[0] : 'NO DATA');

    if (!data || data.length === 0 || !data[0].success) {
      console.error('❌ DEBUG adminAuth.loginAdmin - INVALID_CREDENTIALS');
      throw new Error('INVALID_CREDENTIALS');
    }

    const response = data[0];
    console.log('🔍 DEBUG adminAuth.loginAdmin - response object:', response);
    console.log('🔍 DEBUG adminAuth.loginAdmin - response.role:', response.role, '(tipo:', typeof response.role, ')');
    console.log('🔍 DEBUG adminAuth.loginAdmin - response.session_token:', response.session_token ? '✅ presente' : '❌ ausente');
    console.log('🔍 DEBUG adminAuth.loginAdmin - response.user_id:', response.user_id ? '✅ presente' : '❌ ausente');
    console.log('🔍 DEBUG adminAuth.loginAdmin - response.venue_id:', response.venue_id);

    const result = {
      sessionToken: response.session_token,
      userId: response.user_id,
      role: response.role,
      venueId: response.venue_id
    };

    console.log('🔍 DEBUG adminAuth.loginAdmin - Objeto final a retornar:', result);
    console.log('✅ DEBUG adminAuth.loginAdmin - Login exitoso, retornando resultado');

    return result;
  } catch (error: any) {
    console.error('❌ Error in loginAdmin:', error);
    if (error.message === 'INVALID_CREDENTIALS') throw error;
    throw new Error('AUTH_SERVICE_UNAVAILABLE');
  }
};

/**
 * Valida un token de sesión y retorna el user_id, rol y venue_id si es válido
 */
export const validateAdminSession = async (
  sessionToken: string
): Promise<AdminSessionInfo | null> => {
  try {
    const data = await callRpc('get_admin_session_info', {
      p_session_token: sessionToken
    });

    if (!data || data.length === 0) {
      return null;
    }

    const response = data[0];

    return {
      userId: response.user_id,
      role: response.role,
      venueId: response.venue_id
    };
  } catch (error: any) {
    console.error('Error validating admin session:', error);
    return null;
  }
};

/**
 * Cierra la sesión del admin (v4.1.1 - migrado a RPC)
 */
export const logoutAdmin = async (sessionToken: string): Promise<void> => {
  try {
    await callRpc('admin_logout', {
      p_admin_session_token: sessionToken
    });
  } catch (error: any) {
    console.error('Error logging out admin:', error);
    throw error;
  }
};

// createVenueUser / getVenueUsers / updateVenueUser removidas (v6.11.9.04):
// Los venue users viven en platform-ajupam (predios.ajupam.ar/admin).
// Crearlos desde Liga generaba fantasmas en admin_credentials que no servian
// para nada. Las RPCs (create_venue_user, admin_get_venue_users,
// admin_update_venue_user, admin_delete_venue_user) tambien fueron DROPeadas
// en la migracion 20260630_v6_11_9_04_drop_venue_zombie_rpcs.

/**
 * Actualizar perfil de usuario de predio (v4.1.2)
 * Permite actualizar nombre y teléfono personal
 */
export const updateProfile = async (
  _sessionToken: string,
  userId: string,
  data: { full_name: string; phone: string }
): Promise<void> => {
  const { error } = await (supabase
    .from('profiles') as any)
    .update({
      full_name: data.full_name,
      phone: data.phone,
      updated_at: new Date().toISOString()
    })
    .eq('id', userId);

  if (error) throw handleSupabaseError(error);
};
